import {
  CalculatedCostState,
  Loadable,
  LoadableState,
  OrderCurrencyExchangeBalance,
  OrderExchangeRate,
  OrderExchangeSettlementDate,
} from '@eih-netbroker/common';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export enum OrderExchangeValidationError {
  NotEnoughCurrencyBalance = 'NotEnoughCurrencyBalance',
  NotEnoughCurrencyBalanceToFullOrder = 'NotEnoughCurrencyBalanceToFullOrder',
}

export type OrderExhangeBaseInfo = {
  transactionValueGrossRaw: CalculatedCostState;
  transactionValue: number;
  transactionValueGross: number;
  availabeBalance: number;
  missingValue: number;
};

export type OrderCurrencyExchangeStateContextProps =
  | undefined
  | {
      targetAmount: number | undefined;
      setTargetAmount: React.Dispatch<React.SetStateAction<number | undefined>>;
      sourceAmount: number | undefined;
      setSourceAmount: React.Dispatch<React.SetStateAction<number | undefined>>;

      selectedSource: string | null;
      setSelectedSource: React.Dispatch<React.SetStateAction<string | null>>;

      currencyExchangeBalance: LoadableState<OrderCurrencyExchangeBalance>;
      setCurrencyExchangeBalance: React.Dispatch<React.SetStateAction<LoadableState<OrderCurrencyExchangeBalance>>>;
      exchangeSettlementDate: LoadableState<OrderExchangeSettlementDate>;
      setExchangeSettlementDate: React.Dispatch<React.SetStateAction<LoadableState<OrderExchangeSettlementDate>>>;
      exchangeRate: LoadableState<OrderExchangeRate>;
      setExchangeRate: React.Dispatch<React.SetStateAction<LoadableState<OrderExchangeRate>>>;

      validationError: Set<string>;
      hasError: (error: string) => boolean;
      pushValidationError: (error: string) => void;
      resetAndPushValidationError: (error: string) => void;
      removeValidationError: (error: string) => void;
      clearValidationError: () => void;

      baseInfo: LoadableState<OrderExhangeBaseInfo>;
      setBaseInfo: React.Dispatch<React.SetStateAction<LoadableState<OrderExhangeBaseInfo>>>;
    };

export const OrderCurrencyExchangeStateContext = createContext<OrderCurrencyExchangeStateContextProps>(undefined);

export type OrderCurrencyExchangeStateParams = {
  exchangeRate?: LoadableState<OrderExchangeRate>;
  exchangeSettlementDate?: LoadableState<OrderExchangeSettlementDate>;
};

type OrderCurrencyExchangeStateParamsWithChildren = OrderCurrencyExchangeStateParams & {
  children: React.ReactNode;
};

export const OrderCurrencyExchangeState = ({
  children,
  exchangeRate: _exchangeRate = Loadable.uninitialized,
  exchangeSettlementDate: _exchangeSettlementDate = Loadable.uninitialized,
}: OrderCurrencyExchangeStateParamsWithChildren) => {
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<Set<string>>(new Set<string>());
  const [currencyExchangeBalance, setCurrencyExchangeBalance] = useState<LoadableState<OrderCurrencyExchangeBalance>>(
    Loadable.uninitialized,
  );
  const [exchangeSettlementDate, setExchangeSettlementDate] =
    useState<LoadableState<OrderExchangeSettlementDate>>(_exchangeSettlementDate);
  const [exchangeRate, setExchangeRate] = useState(_exchangeRate);
  const [targetAmount, setTargetAmount] = useState<number | undefined>();
  const [sourceAmount, setSourceAmount] = useState<number | undefined>();
  const [baseInfo, setBaseInfo] = useState<LoadableState<OrderExhangeBaseInfo>>(Loadable.uninitialized);

  const clearValidationError = useCallback(() => setValidationError(new Set<string>()), [setValidationError]);

  const removeValidationError = useCallback((error: string) => {
    setValidationError(prevErrors => {
      return prevErrors.difference(new Set([error]));
    });
  }, []);

  const pushValidationError = useCallback((error: string) => {
    setValidationError(prevErrors => {
      return prevErrors.add(error);
    });
  }, []);

  const resetAndPushValidationError = useCallback(
    (error: string) => {
      setValidationError(new Set([error]));
    },
    [setValidationError],
  );

  const hasError = useCallback(
    (error: string) => {
      return validationError.has(error);
    },
    [validationError],
  );

  const value = useMemo(
    () => ({
      currencyExchangeBalance,
      setCurrencyExchangeBalance,
      exchangeSettlementDate,
      setExchangeSettlementDate,
      exchangeRate,
      setExchangeRate,
      selectedSource,
      setSelectedSource,
      validationError,
      setValidationError,
      pushValidationError,
      resetAndPushValidationError,
      clearValidationError,
      sourceAmount,
      setSourceAmount,
      targetAmount,
      hasError,
      setTargetAmount,
      removeValidationError,
      baseInfo,
      setBaseInfo,
    }),
    [
      currencyExchangeBalance,
      setCurrencyExchangeBalance,
      exchangeSettlementDate,
      setExchangeSettlementDate,
      exchangeRate,
      setExchangeRate,
      selectedSource,
      setSelectedSource,
      validationError,
      pushValidationError,
      removeValidationError,
      resetAndPushValidationError,
      setValidationError,
      clearValidationError,
      sourceAmount,
      setSourceAmount,
      targetAmount,
      hasError,
      setTargetAmount,
      baseInfo,
      setBaseInfo,
    ],
  );

  return (
    <OrderCurrencyExchangeStateContext.Provider value={value}>{children}</OrderCurrencyExchangeStateContext.Provider>
  );
};

export const useOrderCurrencyExchangeState = () => {
  const context = useContext(OrderCurrencyExchangeStateContext);
  if (context === undefined) {
    throw new Error('useOrderCurrencyExchangeState must be used within a OrderCurrencyExchangeStateContext');
  }
  return context;
};
